import React, { useEffect, useRef, useState } from "react";
import { Delete, ScanLine, KeyRound, CheckCircle2, XCircle, Flame } from "lucide-react";
import { endpoints } from "../lib/api";
import { toast } from "sonner";

const PIN_LENGTH = 4;

export default function Checkin() {
  const [tab, setTab] = useState("pin");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { ok, member, today_total, duplicate, method, error }
  const [today, setToday] = useState({ count: 0, entries: [] });
  const [scannerOn, setScannerOn] = useState(false);

  const scannerRef = useRef(null);
  const html5QrcodeRef = useRef(null);
  const scannedLockRef = useRef(false);

  const loadToday = async () => {
    try {
      const { data } = await endpoints.attendanceToday();
      setToday(data);
    } catch {}
  };

  useEffect(() => { loadToday(); }, []);

  // scroll to #today hash
  useEffect(() => {
    if (window.location.hash === "#today") {
      const el = document.getElementById("today-list");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const submitCheckin = async (body) => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await endpoints.attendanceCheckin(body);
      setResult({ ok: true, ...data });
      if (data.duplicate) {
        toast.message(`Already checked in - ${data.member.name}`);
      } else {
        toast.success(`Welcome, ${data.member.name} 💪`);
      }
      loadToday();
    } catch (err) {
      setResult({ ok: false, error: err?.response?.data?.detail || "Check-in failed" });
      toast.error(err?.response?.data?.detail || "Check-in failed");
    } finally {
      setLoading(false);
      setPin("");
      // auto-clear result for next member
      setTimeout(() => setResult(null), 3500);
    }
  };

  const onPinDigit = (d) => {
    if (loading) return;
    if (pin.length >= PIN_LENGTH) return;
    const next = pin + d;
    setPin(next);
    if (next.length === PIN_LENGTH) {
      submitCheckin({ pin: next });
    }
  };

  const onBackspace = () => setPin(pin.slice(0, -1));
  const onClear = () => setPin("");

  // keyboard input for PIN
  useEffect(() => {
    if (tab !== "pin") return;
    const onKey = (e) => {
      if (e.key >= "0" && e.key <= "9") onPinDigit(e.key);
      else if (e.key === "Backspace") onBackspace();
      else if (e.key === "Escape") onClear();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tab, pin, loading]);

  const [scannerError, setScannerError] = useState(null);

  // QR scanner lifecycle
  useEffect(() => {
    if (tab !== "qr" || !scannerOn) return;

    let cancelled = false;
    setScannerError(null);
    const start = async () => {
      try {
        // Pre-flight: getUserMedia gives a clearer error than html5-qrcode
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error("Your browser doesn't support camera access. Try a recent Chrome or Safari.");
        }
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;
        const qr = new Html5Qrcode("qr-reader-region");
        html5QrcodeRef.current = qr;
        scannedLockRef.current = false;
        await qr.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          async (decoded) => {
            if (scannedLockRef.current) return;
            scannedLockRef.current = true;
            await submitCheckin({ code: decoded });
            setTimeout(() => { scannedLockRef.current = false; }, 3000);
          },
          () => {}
        );
      } catch (e) {
        const raw = (e && (e.message || e.toString())) || "Unknown error";
        let friendly = raw;
        if (/permission/i.test(raw) || /NotAllowed/i.test(raw)) {
          friendly = "Camera access was blocked. Please allow camera permissions in your browser, then try again.";
        } else if (/NotFound/i.test(raw) || /no camera/i.test(raw) || /requested device not found/i.test(raw)) {
          friendly = "No camera found on this device. Use the PIN tab instead.";
        } else if (/Insecure/i.test(raw) || /https/i.test(raw) || /secure context/i.test(raw)) {
          friendly = "Camera needs a secure (https) connection. Open this page on the deployed URL or localhost.";
        } else if (/iframe/i.test(raw) || /sandbox/i.test(raw)) {
          friendly = "Camera is blocked by the page sandbox. Open the kiosk in a new tab.";
        }
        toast.error(friendly);
        setScannerError(friendly);
        setScannerOn(false);
      }
    };
    start();

    return () => {
      cancelled = true;
      const qr = html5QrcodeRef.current;
      if (qr) {
        try { qr.stop().then(() => qr.clear()).catch(() => {}); } catch {}
        html5QrcodeRef.current = null;
      }
    };
  }, [tab, scannerOn]);

  // stop scanner when switching tabs
  useEffect(() => {
    if (tab !== "qr") setScannerOn(false);
  }, [tab]);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto">
      <header className="mb-8 lg:mb-10">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ops mb-2">Operate</p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground flex items-center gap-2.5" data-testid="checkin-title">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-ops/10 text-ops">
              <ScanLine className="w-5 h-5" strokeWidth={2.4} />
            </span>
            Tap in. Train on.
          </h1>
          <div className="inline-flex items-center gap-2 bg-card border border-border rounded-full px-3 py-1.5 text-sm" data-testid="today-count-badge">
            <Flame className="w-4 h-4 text-trials" />
            <span className="text-muted-foreground">Today's check-ins:</span>
            <span className="font-semibold text-foreground">{today.count}</span>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Kiosk */}
        <div className="lg:col-span-3 border border-border rounded-2xl bg-card p-6 md:p-8">
          <div className="flex gap-2 mb-6 bg-muted/40 p-1 rounded-lg w-fit">
            <button
              data-testid="tab-pin"
              onClick={() => setTab("pin")}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === "pin" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <KeyRound className="w-4 h-4" /> PIN
            </button>
            <button
              data-testid="tab-qr"
              onClick={() => setTab("qr")}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === "qr" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <ScanLine className="w-4 h-4" /> QR scanner
            </button>
          </div>

          {tab === "pin" && (
            <div className="flex flex-col items-center">
              <p className="text-sm text-muted-foreground mb-4">Enter your 4-digit PIN</p>
              <div className="flex gap-3 mb-8" data-testid="pin-dots">
                {Array.from({ length: PIN_LENGTH }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-14 h-16 border-2 rounded-xl flex items-center justify-center font-display text-3xl font-bold transition-all ${
                      pin[i]
                        ? "border-primary bg-primary/5 text-foreground"
                        : "border-border text-transparent"
                    }`}
                  >
                    {pin[i] ? "●" : "-"}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-3 w-full max-w-xs" data-testid="pin-keypad">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <Key key={n} onClick={() => onPinDigit(String(n))} disabled={loading}>{n}</Key>
                ))}
                <Key onClick={onClear} disabled={loading} variant="ghost">C</Key>
                <Key onClick={() => onPinDigit("0")} disabled={loading}>0</Key>
                <Key onClick={onBackspace} disabled={loading} variant="ghost">
                  <Delete className="w-5 h-5" />
                </Key>
              </div>

              <div className="h-8 mt-4 text-xs text-muted-foreground/70">Keyboard works too.</div>
            </div>
          )}

          {tab === "qr" && (
            <div className="flex flex-col items-center">
              <p className="text-sm text-muted-foreground mb-4">Align the member's QR inside the frame</p>
              {!scannerOn ? (
                <>
                  <button
                    data-testid="start-scanner-btn"
                    onClick={() => setScannerOn(true)}
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-medium px-5 py-3 rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <ScanLine className="w-4 h-4" /> Start camera
                  </button>
                  {scannerError && (
                    <div
                      data-testid="scanner-error"
                      className="mt-4 max-w-md text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded-lg px-3 py-2.5"
                    >
                      <strong className="block mb-0.5">Camera couldn't start</strong>
                      {scannerError}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div
                    id="qr-reader-region"
                    ref={scannerRef}
                    data-testid="qr-reader-region"
                    className="w-full max-w-sm aspect-square rounded-xl overflow-hidden bg-black/5 border border-border"
                  />
                  <button
                    data-testid="stop-scanner-btn"
                    onClick={() => setScannerOn(false)}
                    className="mt-4 text-sm text-muted-foreground hover:text-foreground"
                  >
                    Stop camera
                  </button>
                </>
              )}
            </div>
          )}

          {/* Result banner */}
          {result && (
            <div
              data-testid="checkin-result"
              className={`mt-6 p-4 rounded-xl border flex items-center gap-3 ${
                result.ok
                  ? "bg-emerald-50 border-emerald-200 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-100"
                  : "bg-rose-50 border-rose-200 text-rose-900 dark:bg-rose-950/40 dark:border-rose-800 dark:text-rose-100"
              }`}
            >
              {result.ok ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <XCircle className="w-5 h-5 shrink-0" />}
              <div className="flex-1 min-w-0">
                {result.ok ? (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">
                        {result.duplicate ? "Already checked in" : "Welcome"}, {result.member?.name}
                      </span>
                      {!result.duplicate && result.streak_days > 0 && (
                        <span
                          data-testid="streak-badge"
                          className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-800"
                          title={`${result.streak_days}-day check-in streak`}
                        >
                          <span aria-hidden>🔥</span>
                          {result.streak_days} day{result.streak_days === 1 ? "" : "s"} in a row
                        </span>
                      )}
                    </div>
                    <div className="text-xs opacity-80">
                      {result.method === "qr" ? "Via QR" : "Via PIN"} · Check-in #{result.today_total} today
                    </div>
                  </>
                ) : (
                  <div className="font-semibold">{result.error || "Check-in failed"}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Today list */}
        <aside className="lg:col-span-2 border border-border rounded-2xl bg-card p-5" id="today-list">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-base font-semibold text-foreground">Today</h3>
            <button
              data-testid="refresh-today-btn"
              onClick={loadToday}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Refresh
            </button>
          </div>
          {today.entries.length === 0 ? (
            <div className="text-sm text-muted-foreground/70 py-8 text-center">No check-ins yet today.</div>
          ) : (
            <ol className="divide-y divide-border/60 max-h-[520px] overflow-auto">
              {today.entries.map((e) => (
                <li key={e.id} className="py-3 flex items-center gap-3" data-testid={`checkin-${e.id}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    e.method === "qr" ? "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300" : "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                  }`}>
                    {e.method === "qr" ? <ScanLine className="w-4 h-4" /> : <KeyRound className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{e.member_name}</div>
                    <div className="text-xs text-muted-foreground">{e.plan || "Member"}</div>
                  </div>
                  <div className="text-xs text-muted-foreground/70 tabular-nums">
                    {new Date(e.checked_in_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </aside>
      </div>
    </div>
  );
}

function Key({ children, onClick, disabled, variant = "solid" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`pin-key-${typeof children === "string" || typeof children === "number" ? children : "icon"}`}
      className={`h-16 rounded-xl font-display text-2xl font-semibold transition-all active:scale-95 disabled:opacity-50 ${
        variant === "ghost"
          ? "bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground"
          : "bg-card border border-border hover:border-primary/50 hover:bg-primary/5 text-foreground shadow-sm"
      } flex items-center justify-center`}
    >
      {children}
    </button>
  );
}
